// Recordings table with two views: processed clips (3 s species detections from the clips
// route) and raw recordings (the 1 min uploads from the deployment file list).
// While an upload is PROCESSING both lists are re-read every 2 s until inference settles.
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  BarChart as SpectrogramIcon,
  Replay as RetryIcon,
  InfoOutlined as InfoIcon,
  Warning as WarningIcon,
  Description as FileIcon,
  ChevronRight as ArrowRightIcon,
  FileDownloadOutlined as DownloadIcon,
  SearchOff as SearchOffIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { AudioSource, peekAudioUrl, resolveAudioUrl } from '../resolveAudioUrl';
import AudioAnalyzerPopup from './AudioAnalyzerPopup';
import MarkCompleteButton from './MarkCompleteButton';
import { SegmentedToggle } from './speciesCatalog/DeploymentControls';

interface AcousticFile {
  fileName: string;
  fileId?: string;
  timestamp: string;
  status: 'ANALYZED' | 'PROCESSING' | 'FAILED';
  /** Detection confidence as a 0–100 percentage; uploads not yet clipped have none. */
  confidence?: number;
  clipData?: any;
  detections?: Array<{
    name: string;
    scientificName: string;
    confidence: number;
  }>;
}

interface AcousticDataViewProps {
  onUploadClick?: () => void;
  deploymentId?: string;
  siteName?: string;
  deploymentName?: string;
  deploymentsList?: any[];
  onDeploymentSelect?: (id: string, name: string, siteName: string) => void;
  initialSpeciesFilter?: string;
  refreshKey?: number;
}

type AudioView = 'processed' | 'raw';

const STATUS_BY_FILE_STATUS: Record<string, AcousticFile['status']> = {
  COMPLETED: 'ANALYZED',
  FAILED: 'FAILED',
  PENDING: 'PROCESSING',
  RUNNING: 'PROCESSING',
};

const formatTimestamp = (iso?: string) => {
  if (!iso) return 'N/A';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// Raw recordings carry no species, so no confidence, and open in no analyzer.
const VIEW_COLUMNS: Record<AudioView, { headers: string[]; grid: string; minWidth: string }> = {
  processed: {
    headers: ['File Name', 'Timestamp', 'Duration', 'Confidence', 'Status', 'Action'],
    grid: 'minmax(220px, 2.5fr) minmax(140px, 1.5fr) minmax(80px, 0.8fr) minmax(100px, 0.9fr) minmax(95px, 0.9fr) minmax(120px, 1.1fr)',
    minWidth: '860px',
  },
  raw: {
    headers: ['File Name', 'Timestamp', 'Duration', 'Status', 'Action'],
    grid: 'minmax(260px, 3fr) minmax(160px, 1.5fr) minmax(80px, 0.8fr) minmax(110px, 1fr) minmax(100px, 0.9fr)',
    minWidth: '810px',
  },
};

// Every clip is a 3 s cut and every raw recording a 1 min upload.
const DURATION_LABEL: Record<AudioView, string> = { processed: '00:03', raw: '01:00' };

const SKELETON_ROWS = 6;
const skeletonSx = { bgcolor: 'rgba(255, 255, 255, 0.06)' };

// Rows are shown a page at a time; the next page appears once the table is scrolled to its end.
const PAGE_SIZE = 20;
// The rows are already in memory, so this only keeps the placeholder on screen long enough to read.
const NEXT_PAGE_DELAY_MS = 400;
// How long the pointer rests on a row before its audio starts downloading, so a pass of the
// pointer down the table doesn't start every download.
const WARM_ON_HOVER_DELAY_MS = 150;

// The clips route reports confidence as a 0–1 score; older rows may already be a percentage.
const toPercent = (raw: unknown): number | undefined => {
  if (typeof raw !== 'number' || Number.isNaN(raw)) return undefined;
  return Math.round(raw <= 1 ? raw * 100 : raw);
};

// Stable across the 2 s re-reads, which replace every row object (clip urls are re-signed).
const rowKeyOf = (file: AcousticFile) => file.fileId || file.fileName;

// MM:SS for the elapsed-time counter.
const formatSeconds = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
};

const clickLink = (link: HTMLAnchorElement) => {
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Signed storage urls are cross-origin, where an anchor's download attribute is ignored,
// so the audio is fetched into a blob first. If the bucket refuses that request, the file
// opens in a new tab instead, where the browser can still save it. Exported for the
// analyzer popup's download button.
export const downloadAudio = async (source: AudioSource & { fileName?: string }): Promise<void> => {
  const url = await resolveAudioUrl(source);
  if (!url) throw new Error('Audio unavailable');

  const link = document.createElement('a');
  link.download = source.fileName || 'audio.wav';
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed with status ${response.status}`);
    const blobUrl = URL.createObjectURL(await response.blob());
    link.href = blobUrl;
    clickLink(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  } catch {
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    clickLink(link);
  }
};

// Playing streams the recording from storage, so its first seconds have to arrive before
// anything is heard. Warming a row starts that download ahead of the click. Each budget keeps
// only its most recent rows, so moving down the table can't pull every recording:
// - hover: a row the pointer rests on, which may be a whole 1 min recording.
// - screen: 3 s clips on screen, small enough to fetch for a page at a time.
// A warm element is dropped well before its signed link (an hour) runs out.
const WARM_BUDGETS = { hover: 3, screen: 20 };
type WarmBudget = keyof typeof WARM_BUDGETS;
const WARM_MAX_AGE_MS = 10 * 60 * 1000;

interface WarmAudio {
  audio: Promise<HTMLAudioElement | null>;
  /** The element once its link is in hand, so a click can take it without waiting. */
  element?: HTMLAudioElement | null;
  warmedAt: number;
  budget: WarmBudget;
}

// Oldest first, so the next to go is at the front.
const warmByRow = new Map<string, WarmAudio>();
// Recordings a row's player holds; there is nothing to warm for them.
const heldByPlayer = new Set<string>();

const releaseAudio = (audio: HTMLAudioElement) => {
  audio.pause();
  audio.removeAttribute('src');
  audio.load();
};

const isWarmFresh = (entry: WarmAudio) => Date.now() - entry.warmedAt < WARM_MAX_AGE_MS;

const audioElementFor = (url: string) => {
  const audio = new Audio();
  audio.preload = 'auto';
  audio.src = url;
  return audio;
};

const createAudioFor = async (source: AudioSource): Promise<HTMLAudioElement | null> => {
  const url = await resolveAudioUrl(source);
  return url ? audioElementFor(url) : null;
};

const dropWarm = (key: string) => {
  const entry = warmByRow.get(key);
  if (!entry) return;
  warmByRow.delete(key);
  entry.audio.then((audio) => audio && releaseAudio(audio));
};

/** Starts buffering a recording that is likely to be played next. */
const warmAudio = (key: string, source: AudioSource, budget: WarmBudget = 'hover') => {
  if (heldByPlayer.has(key)) return;
  const existing = warmByRow.get(key);
  if (existing && isWarmFresh(existing)) {
    warmByRow.delete(key);
    warmByRow.set(key, existing);
    return;
  }
  dropWarm(key);

  const known = peekAudioUrl(source);
  const element = known ? audioElementFor(known) : undefined;
  const entry: WarmAudio = {
    audio: element ? Promise.resolve(element) : createAudioFor(source).catch(() => null),
    element,
    warmedAt: Date.now(),
    budget,
  };
  if (!element) {
    entry.audio.then((audio) => {
      entry.element = audio;
    });
  }
  warmByRow.set(key, entry);

  const sameBudget = Array.from(warmByRow.entries()).filter(([, other]) => other.budget === budget);
  for (let i = 0; i < sameBudget.length - WARM_BUDGETS[budget]; i += 1) dropWarm(sameBudget[i][0]);
};

/**
 * An element ready to play straight away, from the click itself: the warm one, or a new one
 * when the link is already in hand. Null when the link still has to be fetched.
 */
const takeAudioNow = (key: string, source: AudioSource): HTMLAudioElement | null => {
  const entry = warmByRow.get(key);
  if (entry?.element && isWarmFresh(entry) && !entry.element.error) {
    warmByRow.delete(key);
    heldByPlayer.add(key);
    return entry.element;
  }
  const known = peekAudioUrl(source);
  if (!known) return null;
  dropWarm(key);
  heldByPlayer.add(key);
  return audioElementFor(known);
};

/** The element to play a recording with: the warm one while it is still good, otherwise a new one. */
const takeAudio = async (key: string, source: AudioSource): Promise<HTMLAudioElement | null> => {
  heldByPlayer.add(key);
  const entry = warmByRow.get(key);
  warmByRow.delete(key);
  try {
    if (entry && isWarmFresh(entry)) {
      const audio = await entry.audio;
      if (audio && !audio.error) return audio;
      if (audio) releaseAudio(audio);
    } else if (entry) {
      entry.audio.then((audio) => audio && releaseAudio(audio));
    }
    const audio = await createAudioFor(source);
    if (!audio) heldByPlayer.delete(key);
    return audio;
  } catch (err) {
    heldByPlayer.delete(key);
    throw err;
  }
};

/** Hands a recording back once its row stops holding it, ending its download. */
const returnAudio = (key: string, audio: HTMLAudioElement | null) => {
  heldByPlayer.delete(key);
  if (audio) releaseAudio(audio);
};

// True once the element comes within 200px of the screen, so rows further down a long table
// don't fetch their audio until they are about to be seen.
const useNearScreen = (ref: React.RefObject<Element>) => {
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const el = ref.current;
    if (near || !el) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) setNear(true);
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, [near, ref]);
  return near;
};

const AudioRowDuration: React.FC<{
  length: string;
  /** Whole seconds played so far, set only while the row is playing. */
  elapsed?: number;
}> = ({ length, elapsed }) => (
  <Box component="span" sx={{ display: 'inline-flex', minHeight: 20, alignItems: 'center' }}>
    {elapsed !== undefined ? (
      <Box component="span" title="Playing" sx={{ color: '#10B981', fontWeight: 600 }}>
        {formatSeconds(elapsed)}
      </Box>
    ) : (
      length
    )}
  </Box>
);

const AudioRowDownload: React.FC<{ file: AcousticFile }> = ({ file }) => {
  const [downloading, setDownloading] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setFailed(false);
    try {
      await downloadAudio(file);
    } catch (err) {
      console.error('Failed to download audio:', err);
      setFailed(true);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box
      onClick={handleDownload}
      title={failed ? 'Download failed, click to retry' : 'Download audio'}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px',
        borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: downloading ? 'default' : 'pointer',
        color: failed ? '#F59E0B' : '#10B981',
        '&:hover': downloading ? {} : { color: '#fff', borderColor: '#10B981', bgcolor: 'rgba(16,185,129,0.1)' },
      }}
    >
      {downloading ? <CircularProgress size={14} sx={{ color: '#10B981' }} /> : <DownloadIcon sx={{ fontSize: 16 }} />}
    </Box>
  );
};

// How long a start may wait on the network before the button shows a spinner. Buffered audio
// starts well inside it, so the button just turns to pause.
const BUFFERING_GRACE_MS = 300;

// Plays from the click itself: the button turns to pause straight away and the audio starts
// from whatever is already buffered (a clip near the screen is fetched ahead). While it plays,
// it reports the elapsed whole seconds to the Duration cell of the same row.
const AudioRowPlayer: React.FC<{
  file: AcousticFile;
  onProgress: (rowKey: string, elapsed: number | null) => void;
}> = ({ file, onProgress }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  // Only once a wait for audio data outlasts the grace period.
  const [isBuffering, setIsBuffering] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  // Rows are re-read every couple of seconds while inference runs, which replaces every
  // row object; only a different recording should stop the one that is playing.
  const rowKey = rowKeyOf(file);
  const activeRow = useRef(rowKey);
  const mounted = useRef(false);
  const alive = useRef(true);
  // Moved on by every pause, so a start still waiting on its link doesn't play after it.
  const startRequest = useRef(0);
  const bufferingTimer = useRef<ReturnType<typeof setTimeout>>();
  const fileRef = useRef(file);
  fileRef.current = file;
  const onProgressRef = useRef(onProgress);
  onProgressRef.current = onProgress;

  const showBufferingSoon = useCallback(() => {
    clearTimeout(bufferingTimer.current);
    bufferingTimer.current = setTimeout(() => setIsBuffering(true), BUFFERING_GRACE_MS);
  }, []);
  const hideBuffering = useCallback(() => {
    clearTimeout(bufferingTimer.current);
    setIsBuffering(false);
  }, []);

  useEffect(() => {
    const previousRow = activeRow.current;
    activeRow.current = rowKey;
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    startRequest.current += 1;
    returnAudio(previousRow, audioRef.current);
    audioRef.current = null;
    onProgressRef.current(previousRow, null);
    hideBuffering();
    setIsPlaying(false);
    setUnavailable(false);
  }, [rowKey, hideBuffering]);

  // A row that leaves the table (another page, view or order) stops its audio, including
  // audio still starting when it left.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      startRequest.current += 1;
      clearTimeout(bufferingTimer.current);
      returnAudio(activeRow.current, audioRef.current);
      audioRef.current = null;
      onProgressRef.current(activeRow.current, null);
    };
  }, []);

  // A 3 s clip carries its own link and is small, so it is fetched as its row nears the screen.
  const nearScreen = useNearScreen(buttonRef);
  const hasOwnLink = Boolean(file.clipData?.url);
  useEffect(() => {
    if (nearScreen && hasOwnLink) warmAudio(rowKey, fileRef.current, 'screen');
  }, [nearScreen, hasOwnLink, rowKey]);

  const fail = () => {
    hideBuffering();
    setIsPlaying(false);
    setUnavailable(true);
    onProgressRef.current(rowKey, null);
  };

  const attach = (audio: HTMLAudioElement) => {
    let reportedSecond = -1;
    audio.onwaiting = showBufferingSoon;
    audio.onplaying = hideBuffering;
    audio.ontimeupdate = () => {
      // Browsers fire this once more on pause, which must not bring the counter back.
      if (audio.paused) return;
      const seconds = Math.floor(audio.currentTime);
      if (seconds === reportedSecond) return;
      reportedSecond = seconds;
      onProgressRef.current(rowKey, seconds);
    };
    audio.onended = () => {
      hideBuffering();
      setIsPlaying(false);
      onProgressRef.current(rowKey, null);
    };
    audio.onerror = () => {
      // Let go of the failed element, so the next press fetches the recording afresh.
      audio.onerror = null;
      if (audioRef.current === audio) audioRef.current = null;
      returnAudio(rowKey, audio);
      fail();
    };
    audioRef.current = audio;
  };

  const start = (audio: HTMLAudioElement) => {
    if (audio.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) showBufferingSoon();
    const request = startRequest.current;
    audio.play().then(
      () => {
        if (alive.current && request === startRequest.current) hideBuffering();
      },
      (err: { name?: string }) => {
        // A pause before the audio could start isn't a failure.
        if (err?.name === 'AbortError' || !alive.current || request !== startRequest.current) return;
        fail();
      },
    );
  };

  const togglePlay = () => {
    if (isPlaying) {
      startRequest.current += 1;
      audioRef.current?.pause();
      hideBuffering();
      setIsPlaying(false);
      // A paused row shows its length again.
      onProgressRef.current(rowKey, null);
      return;
    }

    setUnavailable(false);
    setIsPlaying(true);
    onProgressRef.current(rowKey, Math.floor(audioRef.current?.currentTime ?? 0));

    const ready = audioRef.current ?? takeAudioNow(rowKey, file);
    if (ready) {
      if (ready !== audioRef.current) attach(ready);
      start(ready);
      return;
    }

    // The link still has to be signed: a raw recording whose row hasn't read it yet.
    showBufferingSoon();
    const request = startRequest.current;
    takeAudio(rowKey, file).then(
      (audio) => {
        if (!alive.current || activeRow.current !== rowKey || request !== startRequest.current) {
          returnAudio(rowKey, audio);
          return;
        }
        if (!audio) {
          fail();
          return;
        }
        attach(audio);
        start(audio);
      },
      () => {
        if (alive.current && request === startRequest.current) fail();
      },
    );
  };

  const showSpinner = isPlaying && isBuffering;

  return (
    <Box
      ref={buttonRef}
      onClick={togglePlay}
      title={showSpinner ? 'Loading audio…' : isPlaying ? 'Pause' : unavailable ? 'Audio unavailable' : 'Play'}
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px',
        borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer',
        color: unavailable && !isPlaying ? '#F59E0B' : isPlaying ? '#10B981' : 'rgba(255, 255, 255, 0.5)',
        '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)', bgcolor: isPlaying ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.05)' }
      }}
    >
      {showSpinner ? (
        <CircularProgress size={14} sx={{ color: '#10B981' }} />
      ) : isPlaying ? (
        <PauseIcon sx={{ fontSize: 16 }} />
      ) : (
        <PlayIcon sx={{ fontSize: 16 }} />
      )}
    </Box>
  );
};

const SkeletonRow: React.FC<{ grid: string; isRawView: boolean; index: number; divider: boolean }> = ({
  grid,
  isRawView,
  index,
  divider,
}) => (
  <Box
    aria-hidden
    sx={{
      display: 'grid',
      gridTemplateColumns: grid,
      gap: 2,
      p: 2,
      alignItems: 'center',
      borderBottom: divider ? '1px solid rgba(255, 255, 255, 0.03)' : 'none',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Skeleton variant="circular" width={22} height={22} sx={{ ...skeletonSx, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width={index % 2 === 0 ? '75%' : '60%'} height={20} sx={skeletonSx} />
        <Skeleton variant="text" width={index % 2 === 0 ? '45%' : '35%'} height={14} sx={{ bgcolor: 'rgba(255, 255, 255, 0.03)' }} />
      </Box>
    </Box>
    <Skeleton variant="text" width="65%" height={20} sx={skeletonSx} />
    <Skeleton variant="text" width="40%" height={20} sx={skeletonSx} />
    {!isRawView && <Skeleton variant="text" width="45%" height={20} sx={skeletonSx} />}
    <Skeleton variant="rounded" width={68} height={22} sx={{ ...skeletonSx, borderRadius: '4px' }} />
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
      {Array.from({ length: isRawView ? 2 : 3 }).map((_, i) => (
        <Skeleton key={i} variant="circular" width={28} height={28} sx={skeletonSx} />
      ))}
    </Box>
  </Box>
);

// Placeholder rows at the foot of the table; once they scroll into view the next page replaces them.
// Remounted for every page, so a page that still leaves them on screen asks for the one after it.
const NextPageSkeleton: React.FC<{ rows: number; grid: string; isRawView: boolean; onReveal: () => void }> = ({
  rows,
  grid,
  isRawView,
  onReveal,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const onRevealRef = useRef(onReveal);
  onRevealRef.current = onReveal;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const reveal = () => {
      timer = setTimeout(() => onRevealRef.current(), NEXT_PAGE_DELAY_MS);
    };
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      reveal();
      return () => clearTimeout(timer);
    }
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      reveal();
    });
    observer.observe(el);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, []);

  return (
    <Box ref={ref} role="status" aria-label="Loading more audio files" sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.03)' }}>
      {Array.from({ length: rows }).map((_, idx) => (
        <SkeletonRow key={idx} grid={grid} isRawView={isRawView} index={idx} divider={idx < rows - 1} />
      ))}
    </Box>
  );
};

const AcousticDataView: React.FC<AcousticDataViewProps> = ({
  onUploadClick,
  deploymentId,
  siteName,
  deploymentName,
  deploymentsList,
  onDeploymentSelect,
  initialSpeciesFilter = '',
  refreshKey = 0,
}) => {
  const [speciesFilter, setSpeciesFilter] = useState(initialSpeciesFilter);
  const [minConfidenceFilter, setMinConfidenceFilter] = useState<number>(0);
  const [selectedFileForAnalyzer, setSelectedFileForAnalyzer] = useState<AcousticFile | any>(null);
  const [analyzerOpen, setAnalyzerOpen] = useState(false);
  const [audioView, setAudioView] = useState<AudioView>('processed');
  const [clipFiles, setClipFiles] = useState<AcousticFile[]>([]);
  const [rawFiles, setRawFiles] = useState<AcousticFile[]>([]);
  // Why the raw list could not be read (the file list is admin/employee only); null once it loads.
  const [rawUnavailableReason, setRawUnavailableReason] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Which deployment the rows belong to, so a new deployment shows the skeleton straight
  // away instead of flashing the empty state before its fetch starts.
  const [loadedDeploymentId, setLoadedDeploymentId] = useState<string | undefined>();
  const [sortColumn, setSortColumn] = useState<string | null>('Timestamp');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  // Only the newest fetch may write rows: an older one can land after a deployment switch.
  const latestFetch = useRef(0);
  // Elapsed seconds of each row that is playing, set by its player and shown in its Duration cell.
  const [elapsedByRow, setElapsedByRow] = useState<Record<string, number>>({});
  const handlePlaybackProgress = useCallback((rowKey: string, elapsed: number | null) => {
    setElapsedByRow((prev) => {
      if (elapsed === null) {
        if (!(rowKey in prev)) return prev;
        const next = { ...prev };
        delete next[rowKey];
        return next;
      }
      return prev[rowKey] === elapsed ? prev : { ...prev, [rowKey]: elapsed };
    });
  }, []);

  // Resting the pointer on a row starts buffering its audio, so play is heard sooner.
  const warmTimer = useRef<ReturnType<typeof setTimeout>>();
  const warmRowAudio = useCallback((file: AcousticFile) => {
    clearTimeout(warmTimer.current);
    warmTimer.current = setTimeout(() => warmAudio(rowKeyOf(file), file), WARM_ON_HOVER_DELAY_MS);
  }, []);
  const cancelRowWarm = useCallback(() => clearTimeout(warmTimer.current), []);
  useEffect(() => cancelRowWarm, [cancelRowWarm]);

  const fetchAudioClips = useCallback(async (showLoading = true) => {
    if (!deploymentId) return;

    const requestId = ++latestFetch.current;
    if (showLoading) setLoading(true);
    try {
      const [clipResponse, fileList] = await Promise.all([
        clientService.api.getDeploymentTopUniqueSpeciesClipsApiV1DeploymentsDeploymentIdBiodiversityAudioClipsGet(deploymentId),
        // A refused file list leaves the processed view working on its own.
        clientService.api
          .getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(deploymentId)
          .then((res) => ({ records: res.data ?? [], status: undefined as number | undefined }))
          .catch((err: { status?: number }) => ({ records: null, status: err?.status })),
      ]);
      if (requestId !== latestFetch.current) return;

      const dataDict = clipResponse.data || {};
      const clips: AcousticFile[] = [];

      Object.entries(dataDict).forEach(([speciesName, speciesClips]) => {
        (speciesClips as any[]).forEach((clip: any) => {
          const confidence = toPercent(clip.confidence);
          clips.push({
            fileName: clip.file_name || 'Unknown File',
            timestamp: clip.date && clip.time ? `${clip.date} ${clip.time}` : 'N/A',
            status: 'ANALYZED' as const,
            confidence,
            clipData: clip,
            detections: [{
              name: speciesName,
              scientificName: speciesName,
              confidence: confidence ?? 0,
            }]
          });
        });
      });

      const raw: AcousticFile[] = (fileList.records ?? []).map((record) => ({
        fileName: record.name,
        fileId: record.file_id,
        timestamp: formatTimestamp(record.created_at),
        status: STATUS_BY_FILE_STATUS[record.status] ?? 'PROCESSING',
      }));

      setClipFiles(clips);
      setRawFiles(raw);
      setRawUnavailableReason(
        fileList.records
          ? null
          : fileList.status === 403
            ? 'Raw audio files are available to admins and employees only.'
            : "Couldn't load the raw audio files. Refresh to try again.",
      );
      setLoadError(false);
    } catch (error) {
      if (requestId !== latestFetch.current) return;
      console.error('Failed to fetch audio clips for deployment:', error);
      // A failed background refresh keeps the rows already on screen.
      if (showLoading) {
        setClipFiles([]);
        setRawFiles([]);
        setLoadError(true);
      }
    } finally {
      if (requestId === latestFetch.current) {
        setLoading(false);
        setLoadedDeploymentId(deploymentId);
      }
    }
  }, [deploymentId]);

  useEffect(() => {
    fetchAudioClips();
  }, [fetchAudioClips, refreshKey]);

  // A finished upload lands in the raw list first, so show it there.
  const lastRefreshKey = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === lastRefreshKey.current) return;
    lastRefreshKey.current = refreshKey;
    setAudioView('raw');
  }, [refreshKey]);

  useEffect(() => {
    if (audioView === 'raw' && rawUnavailableReason) setAudioView('processed');
  }, [audioView, rawUnavailableReason]);

  // A column the current view doesn't show can't stay the sort key.
  useEffect(() => {
    if (sortColumn && !VIEW_COLUMNS[audioView].headers.includes(sortColumn)) {
      setSortColumn('Timestamp');
      setSortOrder('desc');
    }
  }, [audioView, sortColumn]);

  const hasProcessingRow = rawFiles.some((f) => f.status === 'PROCESSING');
  useEffect(() => {
    if (!deploymentId || !hasProcessingRow) return undefined;
    const timer = setInterval(() => {
      fetchAudioClips(false);
    }, 2000);
    return () => clearInterval(timer);
  }, [deploymentId, hasProcessingRow, fetchAudioClips]);

  const availableSpecies = useMemo(() => {
    const map = new Map<string, { name: string; scientificName: string }>();
    clipFiles.forEach((f) => {
      (f.detections || []).forEach((d) => {
        if (d.name && !map.has(d.name)) {
          map.set(d.name, { name: d.name, scientificName: d.scientificName || '' });
        }
      });
    });
    return Array.from(map.values());
  }, [clipFiles]);
  const isLoading = loading || (Boolean(deploymentId) && loadedDeploymentId !== deploymentId);
  const noSpeciesDetected = !isLoading && !loadError && availableSpecies.length === 0;

  useEffect(() => {
    const matched = availableSpecies.find(
      (sp) =>
        sp.name.toLowerCase() === initialSpeciesFilter.toLowerCase() ||
        sp.scientificName.toLowerCase() === initialSpeciesFilter.toLowerCase()
    );
    setSpeciesFilter(matched ? matched.name : initialSpeciesFilter);
  }, [initialSpeciesFilter, availableSpecies]);

  const isRawView = audioView === 'raw';
  const viewFiles = isRawView ? rawFiles : clipFiles;
  const columns = VIEW_COLUMNS[audioView];

  const filteredFiles = useMemo(() => {
    // Species and confidence only exist on processed clips.
    if (isRawView) return rawFiles;
    return clipFiles.filter((file) => {
      const matchesSpecies =
        !speciesFilter ||
        (file.detections || []).some(
          (d) =>
            d.name.toLowerCase().includes(speciesFilter.toLowerCase()) ||
            d.scientificName.toLowerCase().includes(speciesFilter.toLowerCase()) ||
            speciesFilter.toLowerCase().includes(d.name.toLowerCase()) ||
            speciesFilter.toLowerCase().includes(d.scientificName.toLowerCase())
        );

      const matchesConfidence =
        minConfidenceFilter === 0 ||
        (file.detections || []).some((d) => d.confidence >= minConfidenceFilter);

      return matchesSpecies && matchesConfidence;
    });
  }, [isRawView, rawFiles, clipFiles, speciesFilter, minConfidenceFilter]);

  const sortedFiles = useMemo(() => {
    if (!sortColumn) return filteredFiles;

    return [...filteredFiles].sort((a, b) => {
      let aVal: any = '';
      let bVal: any = '';

      if (sortColumn === 'Confidence') {
        // Clips without a score sit at the bottom in both directions.
        const aNum = a.confidence;
        const bNum = b.confidence;
        if (aNum === undefined || bNum === undefined) {
          return (aNum === undefined ? 1 : 0) - (bNum === undefined ? 1 : 0);
        }
        aVal = aNum;
        bVal = bNum;
      } else if (sortColumn === 'File Name') {
        aVal = (a.fileName || '').toLowerCase();
        bVal = (b.fileName || '').toLowerCase();
      } else if (sortColumn === 'Timestamp') {
        const timeA = new Date(a.timestamp || 0).getTime();
        const timeB = new Date(b.timestamp || 0).getTime();
        if (!isNaN(timeA) && !isNaN(timeB)) {
          aVal = timeA;
          bVal = timeB;
        } else {
          aVal = (a.timestamp || '').toLowerCase();
          bVal = (b.timestamp || '').toLowerCase();
        }
      } else if (sortColumn === 'Status') {
        aVal = (a.status || '').toLowerCase();
        bVal = (b.status || '').toLowerCase();
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredFiles, sortColumn, sortOrder]);

  // A different list (deployment, view, filter or order) starts again from its first page;
  // the 2 s re-reads keep the pages already shown.
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [deploymentId, audioView, speciesFilter, minConfidenceFilter, sortColumn, sortOrder]);
  const showNextPage = useCallback(() => setVisibleCount((count) => count + PAGE_SIZE), []);
  const pageFiles = useMemo(() => sortedFiles.slice(0, visibleCount), [sortedFiles, visibleCount]);
  const hasMoreFiles = sortedFiles.length > visibleCount;

  // What an empty view says, and where it sends the user next.
  const uploadAction = onUploadClick ? { label: 'Upload audio', onClick: onUploadClick, isUpload: true } : undefined;
  const emptyState: { title: string; body: string; action?: { label: string; onClick: () => void; isUpload?: boolean } } =
    isRawView
      ? {
        title: 'No Raw Audio Files',
        body: 'No recordings have been uploaded to this deployment yet.',
        action: uploadAction,
      }
      : rawFiles.length > 0
        ? {
          title: hasProcessingRow ? 'No Processed Clips Yet' : 'No Species Detected',
          body: hasProcessingRow
            ? 'Species clips appear here once the uploaded recordings finish processing.'
            : "None of this deployment's recordings produced a species detection.",
          action: { label: 'View raw audio files', onClick: () => setAudioView('raw') },
        }
        : {
          title: 'No Species Detected',
          body: 'No species detection data is available for this deployment yet. Upload audio recordings to start detecting species.',
          action: uploadAction,
        };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', pb: 4 }}>
      {/* Filter Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          bgcolor: 'rgba(255, 255, 255, 0.015)',
          border: '1px solid #1E1E1E',
          borderRadius: '10px',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
          {/* Species Dropdown Filter (clips only: raw recordings carry no species) */}
          {!isRawView && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '11px', color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
                Species:
              </Typography>
              <Select
                size="small"
                value={speciesFilter}
                onChange={(e) => setSpeciesFilter(e.target.value)}
                displayEmpty
                disabled={noSpeciesDetected}
                sx={{
                  bgcolor: '#111',
                  color: speciesFilter ? '#10B981' : '#AAA',
                  fontWeight: 600,
                  fontSize: '12px',
                  height: '36px',
                  borderRadius: '8px',
                  border: '1px solid #282828',
                  minWidth: '180px',
                  '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                  '& .MuiSvgIcon-root': { color: '#10B981' },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&.Mui-disabled': { opacity: 0.6 },
                  '& .MuiSelect-select.Mui-disabled': { WebkitTextFillColor: '#888' },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: '#111',
                      border: '1px solid #222',
                      color: '#fff',
                      maxHeight: 280,
                      '& .MuiMenuItem-root': {
                        fontSize: '12px',
                        '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="">{noSpeciesDetected ? 'No Species Detected' : 'All Species'}</MenuItem>
                {availableSpecies.map((sp) => (
                  <MenuItem key={sp.name} value={sp.name}>
                    {sp.name} <span style={{ color: '#666', fontSize: '11px', marginLeft: '6px', fontStyle: 'italic' }}>({sp.scientificName})</span>
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}

          {/* Confidence Score Filter */}
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: '11px', color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
              Confidence:
            </Typography>
            <Select
              size="small"
              value={minConfidenceFilter}
              onChange={(e) => setMinConfidenceFilter(Number(e.target.value))}
              sx={{
                bgcolor: '#111',
                color: minConfidenceFilter > 0 ? '#10B981' : '#AAA',
                fontWeight: 600,
                fontSize: '12px',
                height: '36px',
                borderRadius: '8px',
                border: '1px solid #282828',
                minWidth: '160px',
                '& .MuiSelect-select': { py: 0.5, px: 1.5 },
                '& .MuiSvgIcon-root': { color: '#10B981' },
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#111',
                    border: '1px solid #222',
                    color: '#fff',
                    '& .MuiMenuItem-root': {
                      fontSize: '12px',
                      '&.Mui-selected': { bgcolor: 'rgba(16,185,129,0.15)', color: '#10B981' },
                    },
                  },
                },
              }}
            >
              <MenuItem value={0}>All Confidence Scores</MenuItem>
              <MenuItem value={60}>&ge; 60% Confidence</MenuItem>
              <MenuItem value={75}>&ge; 75% Confidence</MenuItem>
              <MenuItem value={85}>&ge; 85% Confidence</MenuItem>
              <MenuItem value={90}>&ge; 90% Confidence</MenuItem>
            </Select>
          </Box> */}

          {/* Site and Deployment Chips */}
          {siteName && deploymentName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
              <Chip
                label={siteName}
                sx={{
                  bgcolor: '#111',
                  color: '#10B981',
                  fontWeight: 600,
                  fontSize: '12px',
                  border: '1px solid #10B981',
                  borderRadius: '8px',
                  height: '36px',
                }}
              />
              <ArrowRightIcon sx={{ color: '#10B981', fontSize: 18 }} />
              {deploymentsList && deploymentsList.length > 0 && onDeploymentSelect ? (
                <Select
                  value={deploymentId || ''}
                  onChange={(e) => {
                    const selected = deploymentsList.find((d) => d.deployment_id === e.target.value);
                    if (selected && onDeploymentSelect) {
                      onDeploymentSelect(selected.deployment_id, selected.deployment_name, selected.site_name);
                    }
                  }}
                  size="small"
                  sx={{
                    bgcolor: '#111',
                    color: '#10B981',
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '1px dashed #10B981',
                    borderRadius: '8px',
                    height: '36px',
                    '& .MuiSelect-select': { py: 0, px: 2, display: 'flex', alignItems: 'center' },
                    '& .MuiSvgIcon-root': { color: '#10B981' },
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        bgcolor: '#1a1a1a',
                        color: '#eee',
                        border: '1px solid #333',
                        mt: 0.5,
                        borderRadius: '8px',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                        maxHeight: '250px',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: '#444', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
                        '& .MuiMenuItem-root': {
                          fontSize: '12px',
                          py: 1,
                          px: 2,
                          transition: 'background-color 0.2s, color 0.2s',
                        },
                        '& .MuiMenuItem-root:hover': { bgcolor: '#2a2a2a', color: '#10B981' },
                        '& .Mui-selected': { bgcolor: '#2a2a2a !important', color: '#10B981', fontWeight: 600 },
                      },
                    },
                  }}
                >
                  {deploymentsList.map((d) => (
                    <MenuItem key={d.deployment_id} value={d.deployment_id}>
                      {d.deployment_name}
                    </MenuItem>
                  ))}
                </Select>
              ) : (
                <Chip
                  label={deploymentName}
                  sx={{
                    bgcolor: '#111',
                    color: '#10B981',
                    fontWeight: 600,
                    fontSize: '12px',
                    border: '1px dashed #10B981',
                    borderRadius: '8px',
                    height: '36px',
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Filter Stats Badge, Reset & Hierarchy Chips */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isLoading ? (
            <Skeleton variant="text" width={140} height={18} sx={skeletonSx} />
          ) : (
            <Typography sx={{ fontSize: '11px', color: '#888', fontWeight: 600 }}>
              Showing <b>{filteredFiles.length}</b> of {viewFiles.length} {isRawView ? 'audio files' : 'audio clips'} 
            </Typography>
          )}
          {!isRawView && (Boolean(speciesFilter) || minConfidenceFilter > 0) && (
            <Button
              size="small"
              onClick={() => {
                setSpeciesFilter('');
                setMinConfidenceFilter(0);
              }}
              sx={{
                fontSize: '10px',
                fontWeight: 700,
                color: '#EF4444',
                bgcolor: 'rgba(239, 68, 68, 0.08)',
                border: '1px solid rgba(239, 68, 68, 0.25)',
                borderRadius: '6px',
                px: 1,
                py: 0.25,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(239, 68, 68, 0.18)',
                },
              }}
            >
              Clear Filters
            </Button>
          )}
          <MarkCompleteButton deploymentId={deploymentId} />
        </Box>
      </Box>

      {/* View switch: processed 3 s clips or raw 1 min recordings */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <SegmentedToggle<AudioView>
          ariaLabel="Audio file type"
          value={audioView}
          onChange={setAudioView}
          options={[
            { value: 'processed', label: 'TOP CLIPS' },
            {
              value: 'raw',
              label: 'AUDIO FILES',
              disabledReason: rawUnavailableReason ?? undefined,
            },
          ]}
        />
        <Typography sx={{ fontSize: '11px', color: '#777' }}>
          {isRawView
            ? 'Full recordings as uploaded from the device.'
            : 'Short clips where the model detected a species.'}
        </Typography>
      </Box>

      {/* Acoustic Files Table */}
      <Box sx={{ border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: '12px', overflow: 'hidden', background: 'rgba(255, 255, 255, 0.005)' }}>
        <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: '6px' }, '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '4px' } }}>
          <Box sx={{ minWidth: columns.minWidth }}>
            {/* Table Headers */}
            <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: columns.grid,
            gap: 2,
            p: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            bgcolor: 'rgba(255, 255, 255, 0.01)',
          }}
        >
          {columns.headers.map((header) => {
            // Every row in a view has the same length, so Duration has nothing to sort by.
            const isSortable = header !== 'Action' && header !== 'Duration';
            const isActive = sortColumn === header;
            return (
              <Typography
                key={header}
                sx={{
                  color: 'rgba(255, 255, 255, 0.35)',
                  fontSize: '9px',
                  fontWeight: 700,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {header}
                {isSortable && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (isActive) {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortColumn(header);
                        setSortOrder('asc');
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '4px',
                      marginLeft: '2px',
                      color: isActive ? '#10b981' : 'rgba(255, 255, 255, 0.25)',
                      fontSize: '12px',
                    }}
                    title={`Sort by ${header}`}
                  >
                    {isActive ? (sortOrder === 'asc' ? '↑' : '↓') : '↕'}
                  </span>
                )}
              </Typography>
            );
          })}
        </Box>

        {/* Table Rows */}
        {isLoading ? (
          Array.from({ length: SKELETON_ROWS }).map((_, idx) => (
            <SkeletonRow
              key={`skeleton-row-${idx}`}
              grid={columns.grid}
              isRawView={isRawView}
              index={idx}
              divider={idx < SKELETON_ROWS - 1}
            />
          ))
        ) : !deploymentId ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '13px', fontStyle: 'italic' }}>
              Select a Deployment from Map
            </Typography>
          </Box>
        ) : loadError ? (
          <Box role="alert" sx={{ p: 5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <WarningIcon sx={{ color: '#E66767', fontSize: 28 }} />
            <Typography sx={{ color: '#fff', fontSize: '14px', fontWeight: 600 }}>
              Couldn't load audio recordings
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '12px' }}>
              Check your connection and try again.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => fetchAudioClips()}
              sx={{ mt: 1, fontSize: '11px', fontWeight: 700, textTransform: 'none', color: '#EDEDED', borderColor: '#333', '&:hover': { borderColor: '#10B981', color: '#10B981' } }}
            >
              Retry
            </Button>
          </Box>
        ) : viewFiles.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                bgcolor: 'rgba(16, 185, 129, 0.06)',
                border: '1px solid rgba(16, 185, 129, 0.15)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <SearchOffIcon sx={{ color: '#10b981', fontSize: 26, opacity: 0.85 }} />
            </Box>
            <Typography sx={{ color: '#ffffff', fontSize: '15px', fontWeight: 600, mb: 0.5 }}>
              {emptyState.title}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.45)', fontSize: '13px', maxWidth: 420 }}>
              {emptyState.body}
            </Typography>
            {emptyState.action && (
              <Button
                variant="outlined"
                size="small"
                startIcon={emptyState.action.isUpload ? <CloudUploadIcon sx={{ fontSize: 14 }} /> : undefined}
                endIcon={emptyState.action.isUpload ? undefined : <ArrowRightIcon sx={{ fontSize: 16 }} />}
                onClick={emptyState.action.onClick}
                sx={{
                  mt: 2,
                  fontSize: '11px',
                  fontWeight: 700,
                  textTransform: 'none',
                  color: '#10B981',
                  borderColor: 'rgba(16, 185, 129, 0.4)',
                  '&:hover': { borderColor: '#10B981', bgcolor: 'rgba(16, 185, 129, 0.1)' },
                }}
              >
                {emptyState.action.label}
              </Button>
            )}
          </Box>
        ) : filteredFiles.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.3)', fontSize: '13px', fontStyle: 'italic' }}>
              No audio files match the selected species or confidence filters.
            </Typography>
          </Box>
        ) : (
          <>
          {pageFiles.map((file, idx) => {
            const isFailed = file.status === 'FAILED';
            const isProcessing = file.status === 'PROCESSING';

            let statusColor = '#10b981';
            let statusBg = 'rgba(16, 185, 129, 0.08)';
            let statusBorder = 'rgba(16, 185, 129, 0.2)';
            if (isFailed) {
              statusColor = '#ef4444';
              statusBg = 'rgba(239, 68, 68, 0.08)';
              statusBorder = 'rgba(239, 68, 68, 0.2)';
            } else if (isProcessing) {
              statusColor = '#06b6d4';
              statusBg = 'rgba(6, 182, 212, 0.08)';
              statusBorder = 'rgba(6, 182, 212, 0.2)';
            }
            const canDownload = Boolean(file.clipData?.url || file.fileId);

            return (
              <Box
                key={`${audioView}-${rowKeyOf(file)}-${idx}`}
                onPointerEnter={isFailed ? undefined : () => warmRowAudio(file)}
                onPointerLeave={cancelRowWarm}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: columns.grid,
                  gap: 2,
                  p: 2,
                  alignItems: 'center',
                  borderBottom: idx < pageFiles.length - 1 ? '1px solid rgba(255, 255, 255, 0.03)' : 'none',
                  transition: 'background 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.015)',
                  },
                }}
              >
                {/* File Name Cell */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {isFailed ? (
                    <WarningIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                  ) : (
                    <FileIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      title={file.fileName}
                      sx={{
                        color: isFailed ? '#f87171' : '#fff',
                        fontSize: '13px',
                        fontWeight: 600,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-all',
                      }}
                    >
                      {file.fileName}
                    </Typography>
                  </Box>
                </Box>

                {/* Timestamp Cell */}
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px', fontWeight: 500 }}>
                  {file.timestamp}
                </Typography>

                {/* Duration Cell: the elapsed time while the row plays, otherwise its length */}
                <Typography component="div" sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                  <AudioRowDuration length={DURATION_LABEL[audioView]} elapsed={elapsedByRow[rowKeyOf(file)]} />
                </Typography>

                {/* Confidence Cell */}
                {!isRawView && (
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '13px', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>
                    {file.confidence !== undefined ? `${file.confidence}%` : '—'}
                  </Typography>
                )}

                {/* Status Badge Cell */}
                <Box>
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: '4px',
                      border: `1.5px solid ${statusBorder}`,
                      bgcolor: statusBg,
                      color: statusColor,
                      fontSize: '9px',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textAlign: 'center',
                    }}
                  >
                    {file.status}
                  </Box>
                </Box>

                {/* Action Icons Cell */}
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  {isFailed ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' } }}>
                        <RetryIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: 'rgba(255, 255, 255, 0.5)', '&:hover': { color: '#fff', borderColor: 'rgba(255,255,255,0.3)' } }}>
                        <InfoIcon sx={{ fontSize: 16 }} />
                      </Box>
                      {canDownload && <AudioRowDownload file={file} />}
                    </>
                  ) : (
                    <>
                      <AudioRowPlayer file={file} onProgress={handlePlaybackProgress} />
                      {/* The analyzer is for processed clips only */}
                      {!isRawView && (
                        <Box
                          onClick={() => {
                            setSelectedFileForAnalyzer(file);
                            setAnalyzerOpen(true);
                          }}
                          title="Analyze audio in Spectrogram & Waveform viewer"
                          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '50%', border: '1px solid rgba(255, 255, 255, 0.1)', cursor: 'pointer', color: '#10B981', '&:hover': { color: '#fff', borderColor: '#10B981', bgcolor: 'rgba(16,185,129,0.1)' } }}
                        >
                          <SpectrogramIcon sx={{ fontSize: 16 }} />
                        </Box>
                      )}
                      {canDownload && <AudioRowDownload file={file} />}
                    </>
                  )}
                </Box>
              </Box>
            );
          })}
          {hasMoreFiles && (
            <NextPageSkeleton
              key={`${audioView}-${visibleCount}`}
              rows={Math.min(SKELETON_ROWS, sortedFiles.length - visibleCount)}
              grid={columns.grid}
              isRawView={isRawView}
              onReveal={showNextPage}
            />
          )}
          </>
        )}
          </Box>
        </Box>
      </Box>

      {/* Audio Analyzer Popup Modal */}
      <AudioAnalyzerPopup
        open={analyzerOpen && !isRawView}
        onClose={() => setAnalyzerOpen(false)}
        file={selectedFileForAnalyzer}
        deploymentId={deploymentId}
        onDownload={downloadAudio}
      />
    </Box>
  );
};

export default AcousticDataView;
